import React, { useState } from 'react';
import { Mail, Lock, User, Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { TwoFactorSetup } from './TwoFactorSetup';
import { LEGAL_PATHS } from '../legal/paths';

interface RegisterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchToLogin: () => void;
  onRegisterSuccess?: () => void;
}

export const RegisterModal: React.FC<RegisterModalProps> = ({
  isOpen,
  onClose,
  onSwitchToLogin,
  onRegisterSuccess,
}) => {
  const { t } = useTranslation();
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingToken, setPendingToken] = useState('');
  const [showTwoFactorSetup, setShowTwoFactorSetup] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.email.trim()) {
      setError(t('auth:register.errors.emailRequired'));
      return false;
    }
    if (!formData.firstName.trim()) {
      setError(t('auth:register.errors.firstNameRequired'));
      return false;
    }
    if (!formData.lastName.trim()) {
      setError(t('auth:register.errors.lastNameRequired'));
      return false;
    }
    if (formData.password.length < 6) {
      setError(t('auth:register.errors.passwordTooShort'));
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError(t('auth:register.errors.passwordMismatch'));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const outcome = await register(
        formData.email,
        formData.password,
        formData.confirmPassword,
        formData.firstName,
        formData.lastName
      );

      if (outcome.status === 'success') {
        setSuccess(t('auth:register.successMessage'));
        setTimeout(() => {
          onClose();
          onRegisterSuccess?.();
        }, 1500);
      } else {
        // Neue Konten sind nie Admin -> 2FA-Einrichtung ist Pflicht, bevor der Login abgeschlossen ist.
        setPendingToken(outcome.pendingToken);
        setShowTwoFactorSetup(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth:register.errors.generic'));
      console.error('Register error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  if (showTwoFactorSetup) {
    return (
      <>
        <div className="fixed inset-0 bg-black/50 z-40" />
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto overscroll-contain p-4 sm:items-center">
          <div className="card relative my-auto w-full max-w-md animate-slideInUp">
            <div className="mb-6 flex items-center gap-3">
              <img src="/canebase-icon.png" alt="canebase" className="h-9 w-auto object-contain" />
              <h1 className="text-2xl font-bold text-gradient">canebase</h1>
            </div>
            <TwoFactorSetup
              pendingToken={pendingToken}
              onComplete={() => {
                onClose();
                onRegisterSuccess?.();
              }}
            />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto overscroll-contain p-4 sm:items-center">
        <div className="card relative my-auto w-full max-w-md animate-slideInUp">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-4">
              <img src="/canebase-icon.png" alt="canebase" className="h-9 w-auto object-contain" />
              <h1 className="text-2xl font-bold text-gradient">canebase</h1>
            </div>
            <h2 className="text-xl font-bold text-dark-50">{t('auth:register.title')}</h2>
            <p className="text-sm text-dark-400 mt-1">{t('auth:register.subtitle')}</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Error Alert */}
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex gap-3">
                <AlertCircle size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-400">{t('auth:register.errorTitle')}</p>
                  <p className="text-xs text-red-300 mt-1">{error}</p>
                </div>
              </div>
            )}

            {/* Success Alert */}
            {success && (
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 flex gap-3">
                <CheckCircle size={20} className="text-green-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-green-400">{t('auth:register.successTitle')}</p>
                  <p className="text-xs text-green-300 mt-1">{success}</p>
                </div>
              </div>
            )}

            {/* First Name */}
            <div>
              <label className="label-base">{t('auth:register.firstNameLabel')}</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-dark-400" size={18} />
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder={t('auth:register.firstNamePlaceholder')}
                  className="input-base pl-10 bg-dark-700 border-dark-600 text-dark-50 placeholder-dark-400"
                  required
                  disabled={loading}
                />
              </div>
            </div>

            {/* Last Name */}
            <div>
              <label className="label-base">{t('auth:register.lastNameLabel')}</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-dark-400" size={18} />
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder={t('auth:register.lastNamePlaceholder')}
                  className="input-base pl-10 bg-dark-700 border-dark-600 text-dark-50 placeholder-dark-400"
                  required
                  disabled={loading}
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="label-base">{t('auth:register.emailLabel')}</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-dark-400" size={18} />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t('auth:register.emailPlaceholder')}
                  className="input-base pl-10 bg-dark-700 border-dark-600 text-dark-50 placeholder-dark-400"
                  required
                  disabled={loading}
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="label-base">{t('auth:register.passwordLabel')}</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-dark-400" size={18} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder={'\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022'}
                  className="input-base pl-10 pr-10 bg-dark-700 border-dark-600 text-dark-50 placeholder-dark-400"
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-400 hover:text-dark-300 transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <p className="text-xs text-dark-400 mt-1">{t('auth:register.passwordHint')}</p>
            </div>

            {/* Confirm Password */}
            <div>
              <label className="label-base">{t('auth:register.confirmPasswordLabel')}</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-dark-400" size={18} />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder={'\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022'}
                  className="input-base pl-10 pr-10 bg-dark-700 border-dark-600 text-dark-50 placeholder-dark-400"
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-400 hover:text-dark-300 transition-colors"
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Hinweis auf AGB/Datenschutz - neuer Tab, damit die Eingaben im Dialog erhalten bleiben */}
            <p className="text-xs leading-relaxed text-dark-400 pt-2">
              <Trans
                i18nKey="auth:register.legalNotice"
                components={{
                  terms: <a href={LEGAL_PATHS.terms} target="_blank" rel="noopener" className="text-primary-400 hover:text-primary-300" />,
                  privacy: <a href={LEGAL_PATHS.privacy} target="_blank" rel="noopener" className="text-primary-400 hover:text-primary-300" />,
                }}
              />
            </p>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary w-full mt-4"
            >
              {loading ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  {t('auth:register.submitting')}
                </>
              ) : (
                t('auth:register.submit')
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="divider my-6" />

          {/* Login Link */}
          <p className="text-center text-sm text-dark-400">
            {t('auth:register.alreadyRegistered')}{' '}
            <button
              onClick={() => {
                setFormData({
                  email: '',
                  firstName: '',
                  lastName: '',
                  password: '',
                  confirmPassword: '',
                });
                setError('');
                setSuccess('');
                onSwitchToLogin();
              }}
              className="text-primary-400 hover:text-primary-300 font-medium transition-colors"
            >
              {t('auth:register.loginNow')}
            </button>
          </p>

          {/* Close Button */}
          <button onClick={onClose} className="icon-btn absolute right-2 top-2" aria-label={t('common:actions.close')}>
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </>
  );
};
